import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectInput,
  SelectBooleanInput
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock, CardBreakout } from '../../card';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';

const Modes = [
  {
    label: '3 Reminders per Week',
    value: 'T'
  },
  {
    label: '1 Reminder per Week',
    value: 'W'
  },
  {
    label: 'Custom Days',
    value: 'C'
  }
];

type Mode = 'W' | 'T' | 'C';

interface EpxSubAbuseData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  isFixed: boolean;
  mode?: Mode;
  times: string[];
  days: number[];
  inSupportGroups: boolean;
}

type EpxSubAbuseDataRaw = DateSerialized<EpxSubAbuseData, 'start' | 'end'>;

export const EpxSubAbuseDefaultData: EpxSubAbuseData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  isFixed: false,
  mode: 'T',
  times: ['10:00'],
  days: [1, 3, 5],
  inSupportGroups: false
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxSubAbuseFormProps {
  setForm?: (form: FormInstance<EpxSubAbuseDataRaw>) => void;
  onSubmit?: (data: EpxSubAbuseData) => void;
  isDisabled?: boolean;
}

const EpxSubAbuseForm = ({
  setForm,
  onSubmit: _onSubmit,
  isDisabled
}: EpxSubAbuseFormProps): JSX.Element => {
  const serialize = (data: EpxSubAbuseDataRaw): EpxSubAbuseData => {
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      times: data.times.slice(0, 1)
    };
  };
  const deserialize = (data: EpxSubAbuseData): EpxSubAbuseDataRaw => {
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT)
    };
  };
  const defaultData = deserialize(EpxSubAbuseDefaultData);
  const form = new FormInstance<EpxSubAbuseDataRaw, EpxSubAbuseData>(
    defaultData,
    { serialize, deserialize }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxSubAbuseData): void => {
    _onSubmit(data);
  };
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        {(props) => {
          const isFixed = props.values.isFixed;
          const mode = props.values.mode;
          let nDays = null;
          if (!isFixed || mode === 'T') {
            nDays = 3;
          } else if (mode === 'W') {
            nDays = 1;
          }
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                  <Col sm={12}>
                    <SelectLanguage />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Schedule Type"
                      name="isFixed"
                      trueLabel="Fixed Schedule"
                      falseLabel="SMART Schedule"
                      fill
                    />
                  </Col>
                  {isFixed ? (
                    <Col sm={12}>
                      <SelectInput
                        label="Reminder Frequency"
                        name="mode"
                        options={Modes}
                        fill
                      />
                    </Col>
                  ) : null}
                </Row>
              </CardBlock>
              <CardBreakout>
                <Weekday
                  name="days"
                  numItems={nDays}
                  minItems={nDays === null ? 1 : null}
                  inline
                />
              </CardBreakout>
              <CardBlock>
                <Row>
                  <Col md={12}>
                    <SelectTime label="Reminder At" name="times[0]" fill />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Attend Support Groups"
                      name="inSupportGroups"
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxSubAbuseForm;
